export type InlineKind = "text" | "strong" | "code"

export type Inline = {
  kind: InlineKind
  start: number
  end: number
}

export type Heading = { type: "heading"; level: number; start: number; end: number; inlines: Inline[] }
export type Paragraph = { type: "paragraph"; start: number; end: number; inlines: Inline[] }
export type ListItem = { start: number; end: number; bodyStart: number; marker: string; indent: number; inlines: Inline[] }
export type List = { type: "list"; ordered: boolean; start: number; end: number; items: ListItem[] }
export type Code = { type: "code"; lang: string; start: number; end: number; bodyStart: number; bodyEnd: number }
export type Table = { type: "table"; start: number; end: number }
export type Rule = { type: "rule"; start: number; end: number }
export type Block = Heading | Paragraph | List | Code | Table | Rule

type Line = { start: number; end: number; text: string }

const HEADING = /^(#{1,6})\s+/
const FENCE = /^\s*(```|~~~)(.*)$/
const BULLET = /^(\s*)([-*+]|\d+[.)])\s+/
const RULE = /^\s*([-*_])(\s*\1){2,}\s*$/
const TABLE = /^\s*\|/
const HARD_WRAP_WIDTH = 100

function splitLines(text: string): Line[] {
  const lines: Line[] = []
  let start = 0
  for (let index = 0; index <= text.length; index++) {
    if (index === text.length || text[index] === "\n") {
      lines.push({ start, end: index, text: text.slice(start, index) })
      start = index + 1
    }
  }
  return lines
}

export function parseInlines(text: string, start: number, end: number): Inline[] {
  const inlines: Inline[] = []
  let cursor = start
  let plainStart = start
  const pushPlain = (until: number) => {
    if (until > plainStart) inlines.push({ kind: "text", start: plainStart, end: until })
  }
  while (cursor < end) {
    if (text[cursor] === "`") {
      const close = text.indexOf("`", cursor + 1)
      if (close > cursor + 1 && close < end) {
        pushPlain(cursor)
        inlines.push({ kind: "code", start: cursor + 1, end: close })
        cursor = close + 1
        plainStart = cursor
        continue
      }
    }
    if (text.startsWith("**", cursor)) {
      const close = text.indexOf("**", cursor + 2)
      if (close > cursor + 2 && close + 2 <= end) {
        pushPlain(cursor)
        inlines.push({ kind: "strong", start: cursor + 2, end: close })
        cursor = close + 2
        plainStart = cursor
        continue
      }
    }
    cursor++
  }
  pushPlain(end)
  return inlines
}

function contentStart(line: Line, pattern: RegExp): number {
  const match = pattern.exec(line.text)
  return line.start + (match ? match[0].length : 0)
}

function isBlockStart(line: Line): boolean {
  return HEADING.test(line.text) || FENCE.test(line.text) || BULLET.test(line.text) ||
    RULE.test(line.text) || TABLE.test(line.text)
}

export function parsePrompt(text: string): Block[] {
  const lines = splitLines(text)
  const blocks: Block[] = []
  let index = 0
  while (index < lines.length) {
    const line = lines[index]
    if (!line.text.trim()) {
      index++
      continue
    }
    const fence = FENCE.exec(line.text)
    if (fence) {
      let close = index + 1
      while (close < lines.length && !lines[close].text.trim().startsWith(fence[1])) close++
      const last = Math.min(close, lines.length - 1)
      const bodyStart = index + 1 < lines.length ? lines[index + 1].start : line.end
      const bodyEnd = close < lines.length ? Math.max(lines[close].start - 1, bodyStart) : lines[last].end
      blocks.push({ type: "code", lang: fence[2].trim(), start: line.start, end: lines[last].end, bodyStart, bodyEnd })
      index = close + 1
      continue
    }
    const heading = HEADING.exec(line.text)
    if (heading) {
      blocks.push({
        type: "heading", level: heading[1].length, start: line.start, end: line.end,
        inlines: parseInlines(text, contentStart(line, HEADING), line.end),
      })
      index++
      continue
    }
    if (RULE.test(line.text)) {
      blocks.push({ type: "rule", start: line.start, end: line.end })
      index++
      continue
    }
    if (TABLE.test(line.text)) {
      let last = index
      while (last + 1 < lines.length && TABLE.test(lines[last + 1].text)) last++
      blocks.push({ type: "table", start: line.start, end: lines[last].end })
      index = last + 1
      continue
    }
    const bullet = BULLET.exec(line.text)
    if (bullet) {
      const ordered = /\d/.test(bullet[2])
      const items: ListItem[] = []
      while (index < lines.length) {
        const current = lines[index]
        const match = BULLET.exec(current.text)
        if (match && items.length && !match[1].length && /\d/.test(match[2]) !== ordered) break
        if (match) {
          const bodyStart = contentStart(current, BULLET)
          items.push({
            start: current.start, end: current.end, bodyStart, marker: match[2], indent: match[1].length,
            inlines: parseInlines(text, bodyStart, current.end),
          })
          index++
          continue
        }
        const continues = current.text.trim() && /^\s+/.test(current.text) && !isBlockStart(current)
        if (!continues || !items.length) break
        const item = items[items.length - 1]
        item.end = current.end
        item.inlines = parseInlines(text, item.bodyStart, current.end)
        index++
      }
      blocks.push({ type: "list", ordered, start: items[0].start, end: items[items.length - 1].end, items })
      continue
    }
    let last = index
    while (last + 1 < lines.length && lines[last + 1].text.trim() && !isBlockStart(lines[last + 1])) last++
    const rows = lines.slice(index, last + 1)
    if (rows.some((row) => row.text.length > HARD_WRAP_WIDTH)) {
      for (const row of rows) {
        blocks.push({ type: "paragraph", start: row.start, end: row.end, inlines: parseInlines(text, row.start, row.end) })
      }
    } else {
      blocks.push({ type: "paragraph", start: line.start, end: lines[last].end, inlines: parseInlines(text, line.start, lines[last].end) })
    }
    index = last + 1
  }
  return blocks
}

export type OutlineItem = { level: number; title: string; start: number }

export function outline(text: string, blocks: Block[]): OutlineItem[] {
  return blocks
    .filter((block): block is Heading => block.type === "heading")
    .map((block) => ({
      level: block.level,
      start: block.start,
      title: text.slice(block.inlines[0]?.start ?? block.start, block.end).replace(/\*\*|`/g, "").trim(),
    }))
}

export type Highlight = { id: string; start: number; end: number }

export type Piece = { start: number; end: number; highlight: string | null }

export function splitByHighlights(start: number, end: number, highlights: Highlight[]): Piece[] {
  const cuts = new Set<number>([start, end])
  for (const mark of highlights) {
    if (mark.end <= start || mark.start >= end) continue
    cuts.add(Math.max(mark.start, start))
    cuts.add(Math.min(mark.end, end))
  }
  const points = [...cuts].sort((a, b) => a - b)
  const pieces: Piece[] = []
  for (let index = 0; index < points.length - 1; index++) {
    const from = points[index]
    const to = points[index + 1]
    if (to <= from) continue
    const covering = highlights.find((mark) => mark.start <= from && mark.end >= to)
    pieces.push({ start: from, end: to, highlight: covering ? covering.id : null })
  }
  return pieces
}
